import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from "@angular/forms";

/**
 * Extraction mirror of the extension's `extractDomain`
 * (flymsg-extension/src/shared/utils/siteDisabled.ts) so both clients converge
 * on identical strings in the shared `blocked_domains` field: strip an optional
 * scheme, credentials and a leading "www.", then capture up to the first
 * port/path/query/fragment separator.
 */
const DOMAIN_EXTRACT_REGEX = /^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?#\n]+)/;

/**
 * Plausible-hostname gate for what the extraction leaves behind: dot-separated
 * labels of letters/digits/hyphens (no leading/trailing hyphen), at least two
 * labels, max 253 chars total.
 */
const HOSTNAME_REGEX =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * Normalize user input to the bare, www-stripped, lowercased hostname the
 * backend and the extension store in `blocked_domains` — or `null` when the
 * input does not contain a plausible hostname.
 *
 * `normalizeDomain("https://www.Google.com/mail?x=1")` → `"google.com"`;
 * `normalizeDomain("not a domain")` → `null`.
 */
export function normalizeDomain(input: string | null | undefined): string | null {
  if (typeof input !== "string") {
    return null;
  }
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }
  const match = trimmed.match(DOMAIN_EXTRACT_REGEX);
  const host = match ? match[1] : "";
  return HOSTNAME_REGEX.test(host) ? host : null;
}

/**
 * Validator that accepts anything `normalizeDomain` can reduce to a hostname
 * (bare domains, subdomains, full URLs, `www.`-prefixed input). Empty values
 * pass — emptiness is the `required` validator's concern.
 * Error key: `domain`.
 */
export const domainValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  return normalizeDomain(String(value)) === null ? { domain: true } : null;
};

/**
 * Uniqueness across the rows of a `blockList`-style FormArray (each row a
 * FormGroup whose `url` control holds the value), compared on **normalized**
 * values so `https://www.x.com` and `x.com` count as the same entry.
 * Error key: `unique` (same key the template already renders).
 */
export const uniqueDomainValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const normalized = normalizeDomain(control.value);
  if (normalized === null) {
    return null;
  }
  const list = control.parent?.parent;
  if (!(list instanceof FormArray)) {
    return null;
  }
  const duplicated = list.controls.some((row) => {
    const sibling = row.get("url");
    return sibling !== null && sibling !== control && normalizeDomain(sibling.value) === normalized;
  });
  return duplicated ? { unique: true } : null;
};
